import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, CheckCircle, Clock, Copy } from "lucide-react";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";

interface ValidationResponse {
  valid: boolean;
  message: string;
  conflicts: string[];
}

interface WorkingHoursStepProps {
  employeeId: number;
  value: Record<string, string> | null;
  onChange: (value: Record<string, string>) => void;
  onValidation: (isValid: boolean) => void;
}

const DAYS_OF_WEEK = [
  { key: "monday", label: "Monday", short: "Mon" },
  { key: "tuesday", label: "Tuesday", short: "Tue" },
  { key: "wednesday", label: "Wednesday", short: "Wed" },
  { key: "thursday", label: "Thursday", short: "Thu" },
  { key: "friday", label: "Friday", short: "Fri" },
  { key: "saturday", label: "Saturday", short: "Sat" },
  { key: "sunday", label: "Sunday", short: "Sun" },
];

const COMMON_PATTERNS = [
  {
    name: "Full-time (37.5 hours)",
    pattern: {
      monday: "7.5",
      tuesday: "7.5",
      wednesday: "7.5",
      thursday: "7.5",
      friday: "7.5",
      saturday: "0",
      sunday: "0",
    },
  },
  {
    name: "Full-time (40 hours)",
    pattern: {
      monday: "8",
      tuesday: "8",
      wednesday: "8",
      thursday: "8",
      friday: "8",
      saturday: "0",
      sunday: "0",
    },
  },
  {
    name: "Part-time (20 hours)",
    pattern: {
      monday: "4",
      tuesday: "4",
      wednesday: "4",
      thursday: "4",
      friday: "4",
      saturday: "0",
      sunday: "0",
    },
  },
  {
    name: "4-day week (30 hours)",
    pattern: {
      monday: "7.5",
      tuesday: "7.5",
      wednesday: "7.5",
      thursday: "7.5",
      friday: "0",
      saturday: "0",
      sunday: "0",
    },
  },
];

export const WorkingHoursStep: React.FC<WorkingHoursStepProps> = ({
  employeeId,
  value,
  onChange,
  onValidation,
}) => {
  const [workingHours, setWorkingHours] = useState<Record<string, string>>(
    value ||
      DAYS_OF_WEEK.reduce((acc, day) => ({ ...acc, [day.key]: "0" }), {}),
  );
  const [validationResult, setValidationResult] = useState<{
    valid: boolean;
    message: string;
    conflicts: string[];
  } | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  useEffect(() => {
    const hasValidHours = Object.values(workingHours).some(
      (hours) => parseFloat(hours || "0") > 0,
    );

    if (hasValidHours) {
      validateWorkingHours();
    } else {
      setValidationResult(null);
      onValidation(false);
    }
  }, [workingHours]);

  const validateWorkingHours = async () => {
    setIsValidating(true);
    try {
      const response = await ApiService.request<ValidationResponse>(
        EndpointKeys.VALIDATE_SETUP_STEP,
        {
          method: "POST",
          urlParams: { employeeId },
          body: {
            step: "working_hours",
            working_hours: workingHours,
          },
        },
      );

      const result = {
        valid: response.valid,
        message: response.message,
        conflicts: response.conflicts || [],
      };

      setValidationResult(result);
      onValidation(result.valid);
    } catch (error) {
      console.error("Validation error:", error);
      setValidationResult({
        valid: false,
        message: "Failed to validate working hours",
        conflicts: ["Validation service unavailable"],
      });
      onValidation(false);
    } finally {
      setIsValidating(false);
    }
  };

  const handleHoursChange = (day: string, hours: string) => {
    // Allow empty string or valid decimal numbers
    if (hours === "" || /^\d*\.?\d*$/.test(hours)) {
      const newHours = { ...workingHours, [day]: hours };
      setWorkingHours(newHours);
      onChange(newHours);
    }
  };

  const applyPattern = (pattern: Record<string, string>) => {
    setWorkingHours(pattern);
    onChange(pattern);
  };

  const calculateTotalHours = () => {
    return Object.values(workingHours).reduce((total, hours) => {
      return total + (parseFloat(hours || "0") || 0);
    }, 0);
  };

  const getWorkingDaysCount = () => {
    return Object.values(workingHours).filter(
      (hours) => parseFloat(hours || "0") > 0,
    ).length;
  };

  const copyFromDay = (sourceDay: string, targetDay: string) => {
    const hours = workingHours[sourceDay] || "0";
    handleHoursChange(targetDay, hours);
  };

  const totalHours = calculateTotalHours();
  const workingDays = getWorkingDaysCount();

  return (
    <div className="space-y-6">
      {/* Quick Patterns */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">Common patterns:</Label>
        <div className="grid grid-cols-2 gap-2">
          {COMMON_PATTERNS.map((pattern, index) => (
            <Button
              key={index}
              variant="outline"
              size="sm"
              onClick={() => applyPattern(pattern.pattern)}
              className="text-xs"
            >
              {pattern.name}
            </Button>
          ))}
        </div>
      </div>

      {/* Working Hours Grid */}
      <div className="space-y-4">
        <div className="grid gap-3">
          {DAYS_OF_WEEK.map((day) => (
            <Card key={day.key} className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Label className="min-w-[80px] font-medium">
                    {day.label}
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      max="24"
                      placeholder="0"
                      value={workingHours[day.key] || ""}
                      onChange={(e) =>
                        handleHoursChange(day.key, e.target.value)
                      }
                      className="w-20 text-center"
                    />
                    <span className="text-sm text-gray-500">hours</span>
                  </div>
                </div>

                {/* Copy from other days */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 hidden sm:inline">
                    Copy from:
                  </span>
                  <div className="flex gap-1">
                    {DAYS_OF_WEEK.filter((d) => d.key !== day.key).map(
                      (sourceDay) => (
                        <Button
                          key={sourceDay.key}
                          variant="ghost"
                          size="sm"
                          onClick={() => copyFromDay(sourceDay.key, day.key)}
                          className="h-7 px-2 text-xs hover:bg-blue-50 hover:text-blue-700 border border-transparent hover:border-blue-200"
                          title={`Copy hours from ${sourceDay.label}`}
                        >
                          {sourceDay.short}
                        </Button>
                      ),
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Summary */}
      <div className="p-4 bg-gray-50 rounded-lg">
        <div className="flex justify-between items-center mb-2">
          <span className="font-medium">Weekly Summary</span>
          <div className="flex gap-4">
            <Badge variant="outline">{totalHours} hours/week</Badge>
            <Badge variant="outline">{workingDays} working days</Badge>
          </div>
        </div>

        {totalHours > 0 && (
          <div className="text-sm text-gray-600">
            <p>Average: {(totalHours / 7).toFixed(1)} hours/day</p>
            {workingDays > 0 && (
              <p>
                Working days average: {(totalHours / workingDays).toFixed(1)}{" "}
                hours/day
              </p>
            )}
          </div>
        )}
      </div>

      {/* Validation Status */}
      {isValidating && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Validating working hours...</AlertDescription>
        </Alert>
      )}

      {validationResult && !isValidating && (
        <Alert
          className={
            validationResult.valid
              ? "border-green-200 bg-green-50"
              : "border-red-200 bg-red-50"
          }
        >
          {validationResult.valid ? (
            <CheckCircle className="h-4 w-4 text-green-600" />
          ) : (
            <AlertCircle className="h-4 w-4 text-red-600" />
          )}
          <AlertDescription
            className={
              validationResult.valid ? "text-green-800" : "text-red-800"
            }
          >
            <div>
              <p className="font-medium">{validationResult.message}</p>
              {validationResult.conflicts.length > 0 && (
                <ul className="mt-2 list-disc list-inside text-sm">
                  {validationResult.conflicts.map((conflict, index) => (
                    <li key={index}>{conflict}</li>
                  ))}
                </ul>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* UK Working Time Regulations Info */}
      {totalHours > 48 && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <div>
              <p className="font-medium">Working Time Regulations Notice</p>
              <p className="text-sm mt-1">
                This schedule exceeds 48 hours per week. Under UK law, employees
                can opt out of the 48-hour limit, but this requires a signed
                agreement.
              </p>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Helpful Information */}
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="font-medium text-blue-900 mb-2">Tips:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Use decimal hours (e.g., 7.5 for 7 hours 30 minutes)</li>
          <li>• Maximum 24 hours per day allowed</li>
          <li>• Consider UK Working Time Regulations (48 hours/week limit)</li>
          <li>
            • Use the copy buttons to quickly duplicate hours between days
          </li>
        </ul>
      </div>
    </div>
  );
};
